import type { ComponentProps, ForwardedRef, ReactNode } from "react";

import {
  Button,
  Overlay,
  type OpenCtrlRef,
  type Placement,
  Popover,
  type PopoverProps,
  ToolIcon
} from "@theorgchartdev/storybook";

export type ToolbarPopoverProps = {
  className?: string;
  children?: ReactNode;

  triggerTestId?: string;
  icon?: string; // This is optional if otherPopoverProps.reference is provided
  iconOnly?: boolean;
  iconProps?: any; // This could either be IconProps or ToolIconProps
  color?: ComponentProps<typeof Button>["color"];
  disabled?: boolean;

  openCtrlRef?: ForwardedRef<OpenCtrlRef>;
  placement?: Placement;
  otherPopoverProps?: PopoverProps;

  show?: boolean;
  overlayProps?: ComponentProps<typeof Overlay>;

  ariaLabel?: string;
};

const ToolbarPopover = ({
  icon,
  openCtrlRef,
  otherPopoverProps,
  children,
  overlayProps,
  iconProps,
  show = true,
  className = "oc-toolbar-popover",
  placement = "bottom-start",
  iconOnly = false,
  color,
  disabled,
  triggerTestId,
  ariaLabel
}: ToolbarPopoverProps) => {
  const popoverProps = {
    reference: iconOnly ? (
      <Button
        iconOnly
        iconProps={{ ...iconProps, name: icon }}
        color={color}
        size={"small"}
        modifier={"tertiary"}
        dataTestId={triggerTestId}
        disabled={disabled}
      />
    ) : (
      <ToolIcon
        name={icon}
        disabled={disabled}
        {...iconProps}
        dataTestId={triggerTestId}
        aria-label={ariaLabel}
      />
    ),
    options: {
      placement: placement
    },
    arrowOptions: false,
    openCtrlRef: openCtrlRef,
    disabled: disabled,
    ...otherPopoverProps
  };

  return (
    <Popover {...popoverProps}>
      <Overlay show={show} className={className} {...overlayProps}>
        {children}
      </Overlay>
    </Popover>
  );
};

export default ToolbarPopover;
